// App.js
import React, { useState, useEffect, useRef } from 'react';
import mapboxgl from 'mapbox-gl';
import MapboxLanguage from '@mapbox/mapbox-gl-language';
import 'mapbox-gl/dist/mapbox-gl.css';
import {
  AppBar,
  Toolbar,
  Typography,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Box,
  Switch,
  FormControlLabel,
} from '@mui/material';
import { DataGrid } from '@mui/x-data-grid';
import { MAPBOX_ACCESS_TOKEN } from './config';

// Set the Mapbox access token
mapboxgl.accessToken = MAPBOX_ACCESS_TOKEN;

function App() {
  // Refs for the map container, map instance, and language control
  const mapContainerRef = useRef(null);
  const mapRef = useRef(null);
  const languageControlRef = useRef(null);

  // State for the JSON dialog, points data, selected marker id, and language mode.
  const [jsonInput, setJsonInput] = useState('');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [pointsData, setPointsData] = useState([]);
  const [selectedId, setSelectedId] = useState(null);
  const [isChinese, setIsChinese] = useState(false); // false = English, true = Chinese

  // We'll store markers using the row id (which is now the idx string)
  const markersRef = useRef({});

  // Initialize the Mapbox map on mount.
  useEffect(() => {
    mapRef.current = new mapboxgl.Map({
      container: mapContainerRef.current,
      style: 'mapbox://styles/mapbox/streets-v11',
      center: [-74.5, 40],
      zoom: 9,
    });
    mapRef.current.addControl(new mapboxgl.NavigationControl(), 'top-right');

    // When the map loads, instantiate the language plugin.
    // (We do not add it as a visible control; we just use its setLanguage method.)
    mapRef.current.on('load', () => {
      languageControlRef.current = new MapboxLanguage({
        // Use 'zh' for Chinese because that is supported (see :contentReference[oaicite:2]{index=2})
        defaultLanguage: isChinese ? 'zh-Hant' : 'en',
      });
    });

    return () => mapRef.current.remove();
  }, []);

  // Update the map's language when isChinese changes.
  useEffect(() => {
    if (languageControlRef.current && mapRef.current && mapRef.current.isStyleLoaded()) {
      // Get the current style.
      const currentStyle = mapRef.current.getStyle();
      // Generate a new style object with the desired language.
      // The second parameter should be a valid BCP-47 code (e.g. 'en' or 'zh').
      const newStyle = languageControlRef.current.setLanguage(
        currentStyle,
        isChinese ? 'zh-Hant' : 'en'
      );
      // Re-set the map style with diffing disabled.
      // (Disabling diffing ensures the language changes are applied.)
      mapRef.current.setStyle(newStyle, { diff: false });
    }
  }, [isChinese]);

  // Create markers whenever pointsData changes.
  useEffect(() => {
    // Remove any existing markers.
    Object.values(markersRef.current).forEach((marker) => marker.remove());
    markersRef.current = {};

    if (Array.isArray(pointsData)) {
      pointsData.forEach((point) => {
        if (point.coordinates && Array.isArray(point.coordinates)) {
          const el = document.createElement('div');
          el.className = 'marker';
          // Apply circular styling (common for all markers)
          el.style.width = '24px';
          el.style.height = '24px';
          el.style.borderRadius = '50%';
          el.style.border = '2px solid white';
          el.style.display = 'flex';
          el.style.alignItems = 'center';
          el.style.justifyContent = 'center';

          if (point.icon && point.icon.trim() !== "") {
            // Render the provided emoji over the circle
            el.innerHTML = point.icon;
            el.style.fontSize = '16px';
            el.style.backgroundColor = 'blue';
            el.dataset.isIcon = "true";
          } else {
            el.style.backgroundColor = 'blue';
            el.dataset.isIcon = "false";
          }

          // Create a popup for the marker.
          const popupHTML = `
            <h3>${point.point}</h3>
            <p>${point.description}</p>
            ${point.topicGroup ? `<p>Group: ${point.topicGroup}</p>` : ''}
          `;
          const popup = new mapboxgl.Popup({ offset: 25 }).setHTML(popupHTML);

          // Add the marker to the map.
          const marker = new mapboxgl.Marker(el)
            .setLngLat(point.coordinates)
            .setPopup(popup)
            .addTo(mapRef.current);

          markersRef.current[point.idx] = marker;
        }
      });
    }
  }, [pointsData]);

  // Update marker style when the selected marker changes.
  useEffect(() => {
    Object.entries(markersRef.current).forEach(([id, marker]) => {
      const el = marker.getElement();
      if (id === selectedId) {
        if (el.dataset.isIcon === "true") {
          el.style.outline = '3px solid red';
        } else {
          el.style.backgroundColor = 'red';
        }
        el.style.zIndex = '10000';
        if (el.parentNode) {
          el.parentNode.appendChild(el); // Bring marker to front.
        }
      } else {
        if (el.dataset.isIcon === "true") {
          el.style.outline = 'none';
        } else {
          el.style.backgroundColor = 'blue';
        }
        el.style.zIndex = '';
      }
    });
  }, [selectedId]);

  // Handler to parse the JSON input and enrich each point.
  const handleLoadJson = () => {
    try {
      const data = JSON.parse(jsonInput);
      if (!Array.isArray(data)) {
        alert('Invalid JSON: Expected an array of points.');
        return;
      }
      const enrichedData = data.map((point, index) => {
        if (
          !point.description ||
          !point.coordinates ||
          !Array.isArray(point.coordinates) ||
          (!point.point && !point.title)
        ) {
          throw new Error(
            'Each point must have "description", "coordinates", and either "point" or "title".'
          );
        }
        const idxValue = point.idx ? point.idx : (index + 1).toString();
        return {
          id: idxValue,
          idx: idxValue,
          topicGroup: point["topic-group"],
          icon: point.icon,
          point: point.point ? point.point : (point.title ? point.title : ""),
          description: point.description,
          coordinates: point.coordinates,
        };
      });
      setPointsData(enrichedData);
      setDialogOpen(false);
      setSelectedId(null);
    } catch (error) {
      alert(`Invalid JSON. ${error.message}`);
    }
  };

  // Center the map and select the marker when a DataGrid row is clicked.
  const handleRowClick = (params) => {
    const row = params.row;
    if (row && row.coordinates && Array.isArray(row.coordinates)) {
      const currentZoom = mapRef.current.getZoom();
      mapRef.current.flyTo({
        center: row.coordinates,
        zoom: currentZoom,
        essential: true,
      });
      setSelectedId(row.idx);
    }
  };

  // Define DataGrid columns.
  const columns = [
    { field: 'idx', headerName: 'Idx', flex: 5 },
    { field: 'topicGroup', headerName: 'Group', flex: 12.5 },
    { field: 'point', headerName: 'Point', flex: 12.5 },
    {
      field: 'description',
      headerName: 'Description',
      flex: 70,
      renderCell: (params) => (
        <div style={{ whiteSpace: 'normal', wordWrap: 'break-word' }}>
          {params.value}
        </div>
      ),
    },
  ];

  return (
    <div style={{ height: '100vh', width: '100vw' }}>
      {/* AppBar */}
      <AppBar position="static">
        <Toolbar>
          <Typography variant="h6" sx={{ flexGrow: 1 }}>
            Mapbox GL + React + MUI + DataGrid
          </Typography>
          <Button color="inherit" onClick={() => setDialogOpen(true)}>
            Load JSON
          </Button>
        </Toolbar>
      </AppBar>

      {/* Main layout */}
      <Box
        sx={{
          width: '100vw',
          height: 'calc(100vh - 64px)',
          display: 'flex',
          flexDirection: 'column',
          m: 0,
          p: 0,
        }}
      >
        {/* Map container */}
        <Box sx={{ flex: 1, position: 'relative', width: '100%' }}>
          <div ref={mapContainerRef} style={{ width: '100%', height: '100%' }} />

          {/* Language Toggle Switch (top left over the map) */}
          <Box
            sx={{
              position: 'absolute',
              top: 10,
              left: 10,
              zIndex: 2,
              backgroundColor: 'rgba(255, 255, 255, 0.9)',
              borderRadius: 1,
              p: 0.5,
            }}
          >
            <FormControlLabel
              control={
                <Switch
                  checked={isChinese}
                  onChange={(e) => setIsChinese(e.target.checked)}
                  color="primary"
                />
              }
              label={isChinese ? "中文" : "English"}
            />
          </Box>
        </Box>

        {/* DataGrid container */}
        <Box sx={{ height: 300, width: '100%' }}>
          <DataGrid
            rows={pointsData}
            columns={columns}
            pageSize={5}
            rowsPerPageOptions={[5]}
            onRowClick={handleRowClick}
            disableSelectionOnClick
            density="compact"
            rowHeight={32}
          />
        </Box>
      </Box>

      {/* Dialog for pasting JSON */}
      <Dialog
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>Paste JSON</DialogTitle>
        <DialogContent>
          <TextField
            label="JSON"
            placeholder='e.g.,
[
  {
    "idx": "1.1",
    "topic-group": "A",
    "icon": "🌟",
    "point": "Point of Interest 1",
    "description": "This is POI 1, as discussed in the text.",
    "coordinates": [-74.5, 40]
  },
  {
    "idx": "1.2",
    "topic-group": "B",
    "icon": "🚩",
    "point": "Point of Interest 2",
    "description": "This is POI 2, following the narrative timeline.",
    "coordinates": [-74.6, 40.1]
  }
]'
            multiline
            minRows={6}
            fullWidth
            variant="outlined"
            value={jsonInput}
            onChange={(e) => setJsonInput(e.target.value)}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleLoadJson} variant="contained" color="primary">
            Load JSON
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
}

export default App;
